import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import type {
    NotebookMessage,
    NotebookMessageCreate,
    NotebookMessageFileCreate,
} from "@notebook/domain/models/notebook-message.model";
import { NotebookMessageCreateUseCase } from "@notebook/domain/usecases/notebook-message-create.usecase";
import { NotebookMessageFileCreateUseCase } from "@notebook/domain/usecases/notebook-message-file-create.usecase";
import type { MessageModalFormValuesValidated } from "@notebook/presentation/components/diary/message-modal-form.view";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class AddMessageModalViewModel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    notebookId: Nullable<string> = "";

    @observable
    notebookMessageId: Nullable<string> = null;

    constructor(
        @inject(NotebookMessageCreateUseCase)
        private readonly notebookMessageCreateUseCase: NotebookMessageCreateUseCase,
        @inject(NotebookMessageFileCreateUseCase)
        private readonly notebookMessageFileCreateUseCase: NotebookMessageFileCreateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();
    }

    @action
    initViewData(): void {
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async addMessage(
        values: MessageModalFormValuesValidated,
    ): Promise<Nullable<NotebookMessage>> {
        if (!this.notebookId) throw new Error("notebookId is not defined");

        LoadLayoutStore.start();

        const notebookMessageData: NotebookMessageCreate = {
            title: values.title,
            description: values.description,
            time: values.time ?? undefined,
            userId: values.userId,
        };

        const messageAdded = await this.notebookMessageCreateUseCase.execute(
            this.notebookId,
            notebookMessageData,
        );

        if (messageAdded) {
            // Check if files in the form to upload after message creation
            if (values.files?.length) {
                await this.addMessageFile(
                    this.notebookId,
                    messageAdded.id.value,
                    values.files,
                );
            }

            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();

        return messageAdded;
    }

    @action
    async addMessageFile(
        notebookId: string,
        notebookMessageId: string,
        files: File[],
    ): Promise<void> {
        LoadLayoutStore.start();

        const fileData: NotebookMessageFileCreate = {
            files: files,
        };

        const createdFile = await this.notebookMessageFileCreateUseCase.execute(
            notebookId,
            notebookMessageId,
            fileData,
        );

        if (createdFile) {
            // await this.reloadNotebookMessage();
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();
    }

    @action
    setNotebookId(notebookId: string): void {
        runInAction(() => {
            this.notebookId = notebookId;
        });
    }
}
